/* eslint-disable arrow-parens */
import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { SectionHeader } from '../../features/section-components/SectionHeader'
import MarginWrapper from '../../features/wrappers/MarginWrapper'
import styles from './Security.module.scss'
import {
  selectLoginHistoryObject,
  getLoginHistoryError,
  getLoginHistoryStatus,
  getLoginHistoryFilter,
  getLoginHistoryPagination,
  setStartDate,
  setEndDate,
  setPage,
  setRowsPerPage,
} from '../../features/user/LoginHistorySlice'
import {
  formatLastLogin,
  formatApplication,
  formatDeviceSystem,
  formatLocation,
} from './SecurityFormatters'
import JNJFooter from '../../features/jnj-footer/JNJFooter'
import isMobileView from '../../util/MobileView'
import { LoadingIcon } from '../../features/section-components/LoadingIcon'

function SecurityDesktop({ t }) {
  const dispatch = useDispatch()
  const [startDateOpen, setStartDateOpen] = useState(false)
  const [endDateOpen, setEndDateOpen] = useState(false)
  const loginHistoryObj = useSelector(selectLoginHistoryObject)
  const status = useSelector(getLoginHistoryStatus)
  const error = useSelector(getLoginHistoryError)

  const { page, rowsPerPage } = useSelector(getLoginHistoryPagination)
  const { startDate, endDate } = useSelector(getLoginHistoryFilter)

  const rows = loginHistoryObj?.history || []
  const total = loginHistoryObj?.total || 0

  const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1)

  useEffect(() => {
    if (page > maxPage) {
      setPage(maxPage)
    }

    if (total <= rowsPerPage && page !== 0) {
      setPage(0)
    }
  }, [total, rowsPerPage])

  const handleChangePage = (_event, newPage) => {
    dispatch(setPage(newPage))
  }

  const handleChangeRowsPerPage = (event) => {
    dispatch(setRowsPerPage(+event.target.value))
    dispatch(setPage(0))
  }

  const handleStartDateChange = value => {
    dispatch(setStartDate(value))
    dispatch(setPage(0))

    if (value && endDate && dayjs(value).isAfter(dayjs(endDate), 'day')) {
      dispatch(setEndDate(null))
    }
  }

  const handleEndDateChange = value => {
    if (!value) {
      dispatch(setEndDate(value))
      dispatch(setPage(0))
      return
    }

    if (startDate && dayjs(value).isBefore(dayjs(startDate), 'day')) {
      dispatch(setEndDate(null))
      dispatch(setPage(0))
      return
    }

    dispatch(setEndDate(value))
    dispatch(setPage(0))
  }

  // Calcule how many logs are on the page
  const logsOnPage = rows.length

  return (
    <MarginWrapper>
      <section className={styles.securityContainer}>
        <header
          data-testid="security-page-header"
          className={styles.headerBlock}
        >
          <SectionHeader text={t('security-page-header')} size="large" />
          <h4 className={styles.subtitle}>{t('security-page-subtitle')}</h4>
        </header>

        <section className={styles.tableBlock} aria-live="polite">
          {status === 'loading' && (
            <div className={styles.stateMsg}>
              <div className={styles.loadingBox}>
                <LoadingIcon isLoading />
              </div>
            </div>
          )}

          {status === 'failed' && (
            <div className={styles.stateMsg}>{error || t('generic-error')}</div>
          )}

          {status === 'succeeded' && (
            <>
              {!isMobileView() && (
                <div
                  className={styles.filtersBar}
                  role="region"
                  aria-label="Filters"
                >
                  <div className={styles.filtersRow}>
                    <div className={styles.filterGroup}>
                      <span className={styles.filterLabel}>
                        {t('select-start-date')}
                      </span>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={startDate}
                          onChange={handleStartDateChange}
                          open={startDateOpen}
                          onOpen={() => setStartDateOpen(true)}
                          onClose={() => setStartDateOpen(false)}
                          disableFuture
                          maxDate={endDate ? dayjs(endDate) : dayjs()}
                          slotProps={{
                            textField: {
                              placeholder: 'MM / DD / YYYY',
                              className: styles.filterInput,
                              variant: 'outlined',
                              onClick: () => setStartDateOpen(true),
                              inputProps: {
                                'aria-label': 'Start date',
                                'data-testid': 'start-date-input',
                                readOnly: true,
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>

                    <div className={styles.filterGroup}>
                      <span className={styles.filterLabel}>
                        {t('select-end-date')}
                      </span>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={endDate}
                          onChange={handleEndDateChange}
                          open={endDateOpen}
                          onOpen={() => setEndDateOpen(true)}
                          onClose={() => setEndDateOpen(false)}
                          disableFuture
                          maxDate={dayjs()}
                          minDate={startDate ? dayjs(startDate) : undefined}
                          slotProps={{
                            textField: {
                              placeholder: 'MM / DD / YYYY',
                              className: styles.filterInput,
                              variant: 'outlined',
                              onClick: () => setEndDateOpen(true),
                              inputProps: {
                                'aria-label': 'End date',
                                'data-testid': 'end-date-input',
                                readOnly: true,
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                </div>
              )}

              {Array.isArray(rows) && rows.length === 0 ? (
                <div className={styles.stateMsgNoFound}>
                  {t('security-page-message')}
                </div>
              ) : (
                <TableContainer className={styles.tableWrap}>
                  <Table className={styles.loginHistoryTable}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={styles.colLastLogin}>
                          {t('security-login-history-lastLogin')}
                        </TableCell>
                        <TableCell className={styles.colApplication}>
                          {t('security-login-history-eventType')}
                        </TableCell>
                        <TableCell className={styles.colDeviceSystem}>
                          {t('security-login-history-deviceSystem')}
                        </TableCell>
                        <TableCell className={styles.colLocation}>
                          {t('security-login-history-location')}
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {rows.map((item) => (
                        <TableRow
                          key={`${item?.published || 'row'}-${item.published}`}
                        >
                          <TableCell>
                            {formatLastLogin(item?.published)}
                          </TableCell>
                          <TableCell>
                            {formatApplication(item?.eventType)}
                          </TableCell>
                          <TableCell>{formatDeviceSystem(item)}</TableCell>
                          <TableCell>{formatLocation(item)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <TablePagination
                    component="div"
                    className={styles.tablePagination}
                    data-testid="logs-per-page-of"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[25, 50]}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t('logs-per-page')}
                    labelDisplayedRows={({ from, count }) =>
                      `${t('logs-displaying')} ${from} -  ${from + logsOnPage - 1} ${t(
                        'logs-per-page-of',
                      )} ${count}`}
                    sx={{ color: '#564C47', marginTop: '44px' }}
                  />
                </TableContainer>
              )}
            </>
          )}
        </section>
      </section>

      <div className={styles.jnjFooter}>
        <JNJFooter text={t('FooterText')} footerSize="medium" />
      </div>
    </MarginWrapper>
  )
}

SecurityDesktop.propTypes = {
  t: PropTypes.func.isRequired,
}

export default SecurityDesktop
